/**
 * CheckKey → droom component registry (KYCID-FSD-00013 §8).
 *
 * DIGILOCKER_AADHAAR renders the existing Aadhaar rail screen; every other
 * registered check with no bespoke component yet falls back to the honest
 * MissingCheckStep (continue-with-agent) — NEVER a white screen for an
 * unregistered key.
 */

import type { ComponentType } from 'react';
import type { CheckKey } from '@/types';
import { CUSTOMER_FACING_PRECALL_CHECKS } from '../checkPlan';
import type { StepProps } from '../types';
import { Aadhaar } from './Aadhaar';
import { MissingCheckStep } from './MissingCheckStep';
import { PanStep } from './PanStep';

/** Bespoke components for checks that have a real droom screen. */
/**
 * PH-1 (KYCID·00001-00006-00002): only AUTHORITATIVE-SOURCE checks render as customer pre-call steps.
 *
 * DOC_CAPTURE / SELFIE / SELFIE_WITH_DOC were removed together with their entries in
 * CUSTOMER_FACING_PRECALL_CHECKS — the coverage invariant below requires the two sets to match
 * exactly, which is precisely the guard that keeps a half-done phase move from shipping. Their
 * components remain in the tree, unreferenced, because a tenant may still legally pin the PRE_CALL
 * variant during migration; re-adding a key here restores the step without rebuilding it.
 */
export const CHECK_COMPONENTS: Partial<Record<CheckKey, ComponentType<StepProps>>> = {
  DIGILOCKER_AADHAAR: Aadhaar,
  // Owner item 3: the source-of-record PAN check, runnable pre-call when the plan pins it there.
  PAN_SOURCE: PanStep,
};

// Coverage invariant (KYCID·00001-00005-00017): the set that decides which PRE_CALL checks RENDER as customer
// steps (checkPlan.CUSTOMER_FACING_PRECALL_CHECKS) must exactly match the checks that HAVE a bespoke component
// here — else buildFlowOrder would hide a real customer step, or splice an agent-driven one. droom has no test
// runner, so this is a dev-load assertion (stripped from the prod build).
if (import.meta.env.DEV) {
  for (const key of Object.keys(CHECK_COMPONENTS) as CheckKey[]) {
    if (!CUSTOMER_FACING_PRECALL_CHECKS.has(key)) {
      throw new Error(
        `checkRegistry: '${key}' has a bespoke component but is missing from CUSTOMER_FACING_PRECALL_CHECKS ` +
          `(checkPlan) — it would be hidden from the customer flow.`,
      );
    }
  }
  CUSTOMER_FACING_PRECALL_CHECKS.forEach((key) => {
    if (!CHECK_COMPONENTS[key]) {
      throw new Error(
        `checkPlan: '${key}' is customer-facing but has no bespoke droom component (checkRegistry) — it ` +
          `would render the MissingCheckStep placeholder.`,
      );
    }
  });
}

/** Resolve the component for a check key — MissingCheckStep for the unregistered. */
export function checkComponentFor(key: CheckKey): ComponentType<StepProps> {
  return CHECK_COMPONENTS[key] ?? MissingCheckStep;
}
